import type { SetStateAction } from "react";

/**
 * A utility function that creates a state setter which can handle both direct values and callbacks.
 * This is useful for Zustand stores where you want to support both value and callback-based updates.
 *
 * @param set - The set function from Zustand store
 * @param key - The key in the state object to update
 * @returns A function that can accept either a value or a callback
 */
export const createStateSetter = <
  TValue,
  TState extends Record<string, unknown>
>({
  set,
  key
}: {
  set: (fn: (state: TState) => Partial<TState>) => void;
  key: keyof TState & string;
}) => {
  return (valueOrCallback: SetStateAction<TValue>) =>
    set(
      state =>
        ({
          [key]:
            typeof valueOrCallback === "function"
              ? (valueOrCallback as (prev: TValue) => TValue)(
                  state[key] as TValue
                )
              : valueOrCallback
        }) as Partial<TState>
    );
};

/**
 * A utility function that creates a state setter for arrays which ensures uniqueness of elements
 * and can handle both direct values and callbacks.
 *
 * @param set - The set function from Zustand store
 * @param key - The key in the state object to update
 * @returns A function that can accept either an array or a callback returning an array
 */
export const createUniqueArraySetter = <
  TValue,
  TState extends Record<string, unknown>
>({
  set,
  key
}: {
  set: (fn: (state: TState) => Partial<TState>) => void;
  key: keyof TState & string;
}) => {
  return (valueOrCallback: SetStateAction<TValue[]>) =>
    set(
      state =>
        ({
          [key]: Array.from(
            new Set(
              typeof valueOrCallback === "function"
                ? (valueOrCallback as (prev: TValue[]) => TValue[])(
                    state[key] as TValue[]
                  )
                : valueOrCallback
            )
          )
        }) as Partial<TState>
    );
};
